import { useState, useEffect } from'react'
import { useNavigate } from'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from'@/components/ui/Card'
import { Button } from'@/components/ui/Button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from'@/components/ui/Tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from'@/components/ui/Table'
import { Badge } from'@/components/ui/Badge'
import { 
 Shield, Users, Cpu, DollarSign, Activity,
 RefreshCw, AlertCircle, CheckCircle2, XCircle,
 TrendingUp, BarChart3, Settings, PlayCircle,
 Plus, Trash2, ChevronUp, ChevronDown,
 MessageSquarePlus, Bug, Lightbulb, Archive, CircleDot
} from'lucide-react'
import { DEFAULT_APP_HOME } from'@/config/routes'
import { AiSpendTab } from'@/components/admin/AiSpendTab'
import { BRAND } from '../config/brand'

interface OverviewData {
 total: {
 requests: number
 input_tokens: number
 output_tokens: number
 total_tokens: number
 cost: number
 }
 by_provider: Array<{
 provider: string
 requests: number
 input_tokens: number
 output_tokens: number
 cost: number
 }>
 by_model: Array<{
 provider: string
 model: string
 requests: number
 input_tokens: number
 output_tokens: number
 cost: number
 avg_response_time_ms: number
 }>
 by_agent?: Array<{
 agent: string
 requests: number
 input_tokens: number
 output_tokens: number
 cost: number
 }>
}

interface UserUsage {
 user_id: number
 username: string
 email: string
 requests: number
 input_tokens: number
 output_tokens: number
 total_tokens: number
 cost: number
 last_used: string | null
}

interface RecentRequest {
 id: number
 timestamp: string
 user: {
 id: number
 username: string
 email: string
 }
 provider: string
 model: string
 operation: string
 agent: string
 input_tokens: number
 output_tokens: number
 total_tokens: number
 cost: number
 input_cost: number
 output_cost: number
 response_time_ms: number
 success: boolean
 error: string | null
}

interface AccessRequest {
 id: number
 username: string
 email: string
 approval_status: string
 created_at?: string
 approved_at?: string | null
 approved_by?: string | null
}

interface SystemStats {
 total_users: number
 active_users_30d: number
}

interface LLMModel {
 id: string
 name: string
 description: string
 max_tokens: number
 input_price: number
 output_price: number
 supports_reasoning?: boolean
}

interface LLMProvider {
 name: string
 models: LLMModel[]
}

// Priority-ordered model selection (new) with prompt_suffix support
interface LLMModelEntry {
 provider: string
 model: string
 prompt_suffix?: string // Model-specific prompt tweaks
 max_tokens?: number
 temperature?: number
 timeout_seconds?: number
}

/**
 * The gateway's class pools, keyed by class name.
 *
 * Not a fixed set of stages. This was `{ router, planner, scheduler }`, three
 * keys hardcoded here and in the render, which is why the tab could not show
 * the `medium` and `reasoning` pools that had existed on the server for some
 * time — a class the UI has no key for is a class no operator can see.
 */
type LLMClassPools = Record<string, LLMModelEntry[]>

/**
 * What each stage namespace belongs to, for anyone who did not write it.
 *
 * A purpose string is a wire identifier, not a name: `hierarchy.duplicate_judge`
 * says nothing about whether changing it affects the chat or a background job,
 * which is the first thing you need to know before touching it.
 */
const NAMESPACE_LABELS: Record<string, { name: string; desc: string }> = {
 compass: { name: BRAND.assistant, desc:'the chat pipeline — one turn runs these in order' },
 guidance: { name:'Guidance', desc:'the previous chat runtime, still reachable' },
 hierarchy: { name:'Hierarchy', desc:'life-map maintenance outside the chat' },
 journey: { name:'Journey engine', desc:'plan and task breakdown' },
 motivator: { name:'Motivator', desc:'scheduled nudges' },
 prioritization: { name:'Prioritisation', desc:'ordering the day' },
 scheduler: { name:'Scheduler', desc:'placing work on the calendar' },
 session: { name:'Session', desc:'summarising a finished conversation' },
}

/**
 * What a pass does, for the ones where the name does not say it.
 *
 * Deliberately incomplete. A row with no note shows only its purpose string,
 * which is honest; a generic "custom pass" on two thirds of the list would
 * just be noise standing where information should be.
 */
const PASS_NOTES: Record<string, string> = {
'compass.front_door':'Reads the message and picks the reply mode.',
'compass.directions':'Names the roots an area needs, as a closed set.',
'compass.interpreter':'Writes the reflection and guidance the user reads.',
'compass.segmenter':'Groups the message by area so extraction reads it a piece at a time.',
'compass.extractor':'Pulls the dreams, goals and leaves out of the message. Runs once per segment.',
'compass.fusion':'Reconciles proposals against the map the user already has.',
'compass.bounce':'Second look at a reply before it is sent.',
'compass.recompose':'Rewrites a reply the second look rejected.',
}

interface FeedbackItem {
 id?: number
 _id?: number
 user_id: number
 category: string
 expected: string
 actual?: string
 improvement?: string
 severity?: string
 status: string
 submitted_at: string
 context?: {
 page_route?: string
 page_title?: string
 user_agent?: string
 viewport?: string
 is_mobile?: boolean
 timestamp?: string
 page_context?: Record<string, unknown>
 }
}

interface LLMTestResult {
 success: boolean
 response?: string
 response_time_ms?: number
 error?: string
}

export function AdminPanel() {
 const navigate = useNavigate()
 const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
 const [loading, setLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [days, setDays] = useState(30)
 
 const [overview, setOverview] = useState<OverviewData | null>(null)
 const [systemStats, setSystemStats] = useState<SystemStats | null>(null)
 const [userUsage, setUserUsage] = useState<UserUsage[]>([])
 const [recentRequests, setRecentRequests] = useState<RecentRequest[]>([])
 const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
 const [accessLoading, setAccessLoading] = useState(false)
 const [purgingUserId, setPurgingUserId] = useState<number | null>(null)
 
 // Feedback state
 const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([])
 const [feedbackLoading, setFeedbackLoading] = useState(false)
 const [feedbackFilter, setFeedbackFilter] = useState<string>('all')
 const [expandedFeedback, setExpandedFeedback] = useState<Set<number>>(new Set())
 const [archiving, setArchiving] = useState(false)
 const [archivedItems, setArchivedItems] = useState<(FeedbackItem & { archived_at?: string })[]>([])
 const [showArchived, setShowArchived] = useState(false)
 const [archivedLoading, setArchivedLoading] = useState(false)

 // LLM Configuration state (using ordered format)
 const [llmProviders, setLlmProviders] = useState<Record<string, LLMProvider>>({})
 // Empty until the gateway answers. The previous seed here was a plausible
 // list of models that had never been the live config — the tab showed it
 // before the fetch landed and after the fetch failed, so a pool that could
 // not be read was indistinguishable from one that was serving.
 const [llmConfig, setLlmConfig] = useState<LLMClassPools>({})
 const [llmAliases, setLlmAliases] = useState<Record<string, string>>({})
 const [llmRequired, setLlmRequired] = useState<string[]>([])
 /** `defaults` means nothing has been saved and the shipped pools are serving. */
 const [llmSource, setLlmSource] = useState<string | null>(null)
 const [llmSaving, setLlmSaving] = useState(false)
 const [llmTestResult, setLlmTestResult] = useState<Record<string, LLMTestResult>>({})
 const [llmTesting, setLlmTesting] = useState<Record<string, boolean>>({})
 
 // Prompt suffix editing state
 const [editingSuffix, setEditingSuffix] = useState<string | null>(null) //"planner-0","router-1", etc.
 const [suffixDraft, setSuffixDraft] = useState<string>('')
 
 // Toast notification state
 const [toastMessage, setToastMessage] = useState<{ type:'success' |'error', text: string } | null>(null)
 
 // Check admin status
 useEffect(() => {
 const checkAdmin = async () => {
 try {
 const response = await fetch('/api/admin/check', {
 credentials:'include'
 })
 const data = await response.json()
 
 if (data.success && data.is_admin) {
 setIsAdmin(true)
 fetchAllData()
 } else {
 setIsAdmin(false)
 setLoading(false)
 }
 } catch (err) {
 setIsAdmin(false)
 setLoading(false)
 }
 }
 
 checkAdmin()
 }, [])

 useEffect(() => {
 if (isAdmin) fetchAllData()
 }, [days])
 
 const fetchAllData = async () => {
 setLoading(true)
 setError(null)
 
 const safeFetch = async (url: string) => {
 try {
 const res = await fetch(url, { credentials:'include' })
 if (!res.ok) return { success: false, _status: res.status }
 return await res.json()
 } catch {
 return { success: false, _fetchError: true }
 }
 }

 const [overviewData, usersData, requestsData, accessData, modelsData, configData] = await Promise.all([
 safeFetch(`/api/admin/overview?days=${days}`),
 safeFetch(`/api/admin/users?days=${days}`),
 safeFetch('/api/admin/requests?limit=100'),
 safeFetch('/api/admin/access-requests?status=pending'),
 safeFetch('/api/admin/llm/models'),
 safeFetch('/api/admin/llm/config'),
 ])
 
 const failures: string[] = []

 if (overviewData.success) {
 setOverview(overviewData.overview)
 setSystemStats(overviewData.system)
 } else {
 failures.push('overview')
 }
 
 if (usersData.success) {
 setUserUsage(usersData.users)
 } else {
 failures.push('users')
 }
 
 if (requestsData.success) {
 setRecentRequests(requestsData.requests)
 } else {
 failures.push('requests')
 }

 if (accessData.success) {
 setAccessRequests(accessData.requests || [])
 } else {
 failures.push('access-requests')
 }
 
 if (modelsData.success) {
 setLlmProviders(modelsData.providers)
 } else {
 failures.push('llm-models')
 }
 
 if (configData.success) {
 setLlmConfig(configData.classes || {})
 setLlmAliases(configData.aliases || {})
 setLlmRequired(configData.required_classes || [])
 setLlmSource(configData.source || null)
 } else {
 failures.push('llm-config')
 }

 if (failures.length > 0 && failures.length === 6) {
 setError('Failed to load admin data')
 } else if (failures.length > 0) {
 setError(`Partial load — failed: ${failures.join(',')}`)
 }

 setLoading(false)
 }

 const fetchFeedback = async (statusFilter?: string) => {
 setFeedbackLoading(true)
 try {
 const params = new URLSearchParams({ limit:'200' })
 if (statusFilter && statusFilter !=='all') params.set('status', statusFilter)
 const res = await fetch(`/api/feedback?${params}`, { credentials:'include' })
 const data = await res.json()
 if (data.success) {
 setFeedbackItems(data.feedback || [])
 }
 } catch { /* ignore */ } finally {
 setFeedbackLoading(false)
 }
 }

 const updateFeedbackStatus = async (docId: number, newStatus: string) => {
 try {
 const res = await fetch(`/api/feedback/${docId}/status`, {
 method:'PATCH',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify({ status: newStatus }),
 })
 const data = await res.json()
 if (data.success) {
 setFeedbackItems(prev => prev.map(f => {
 const fid = f.id ?? f._id
 return fid === docId ? { ...f, status: newStatus } : f
 }))
 setToastMessage({ type:'success', text: `Feedback #${docId} marked ${newStatus}` })
 setTimeout(() => setToastMessage(null), 3000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to update status' })
 setTimeout(() => setToastMessage(null), 5000)
 }
 }

 const toggleFeedbackExpand = (id: number) => {
 setExpandedFeedback(prev => {
 const next = new Set(prev)
 if (next.has(id)) next.delete(id)
 else next.add(id)
 return next
 })
 }

 const deleteFeedback = async (docId: number) => {
 if (!confirm('Permanently delete this feedback item?')) return
 try {
 const res = await fetch(`/api/feedback/${docId}`, {
 method:'DELETE',
 credentials:'include',
 })
 const data = await res.json()
 if (data.success) {
 setFeedbackItems(prev => prev.filter(f => (f.id ?? f._id) !== docId))
 setToastMessage({ type:'success', text: `Feedback #${docId} deleted` })
 setTimeout(() => setToastMessage(null), 3000)
 } else {
 setToastMessage({ type:'error', text: data.error || 'Delete failed' })
 setTimeout(() => setToastMessage(null), 5000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to delete feedback' })
 setTimeout(() => setToastMessage(null), 5000)
 }
 }

 const archiveResolved = async () => {
 setArchiving(true)
 try {
 const res = await fetch('/api/feedback/archive', {
 method:'POST',
 credentials:'include',
 })
 const data = await res.json()
 if (data.success) {
 const count = data.archived_count ?? 0
 setToastMessage({ type:'success', text: `Archived ${count} resolved/dismissed item${count !== 1 ?'s' :''}` })
 setTimeout(() => setToastMessage(null), 4000)
 fetchFeedback(feedbackFilter)
 if (showArchived) fetchArchived()
 } else {
 setToastMessage({ type:'error', text: data.error || 'Archive failed' })
 setTimeout(() => setToastMessage(null), 5000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to archive feedback' })
 setTimeout(() => setToastMessage(null), 5000)
 } finally {
 setArchiving(false)
 }
 }

 const fetchArchived = async () => {
 setArchivedLoading(true)
 try {
 const res = await fetch('/api/feedback/archived?limit=200', { credentials:'include' })
 const data = await res.json()
 if (data.success) {
 setArchivedItems(data.feedback || [])
 }
 } catch { /* ignore */ } finally {
 setArchivedLoading(false)
 }
 }

 const refreshAccessRequests = async () => {
 setAccessLoading(true)
 try {
 const res = await fetch('/api/admin/access-requests?status=pending', { credentials:'include' })
 const data = await res.json()
 if (data.success) {
 setAccessRequests(data.requests || [])
 } else {
 setToastMessage({ type:'error', text: data.error ||'Failed to load access requests' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to load access requests' })
 setTimeout(() => setToastMessage(null), 6000)
 } finally {
 setAccessLoading(false)
 }
 }

 const approveAccess = async (userId: number) => {
 try {
 const res = await fetch(`/api/admin/access-requests/${userId}/approve`, {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' }
 })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setToastMessage({ type:'success', text: `Approved user ${userId}` })
 setTimeout(() => setToastMessage(null), 4000)
 await refreshAccessRequests()
 } else {
 setToastMessage({ type:'error', text: data.error ||'Failed to approve user' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to approve user' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 }

 const rejectAccess = async (userId: number) => {
 try {
 const res = await fetch(`/api/admin/access-requests/${userId}/reject`, {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' }
 })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setToastMessage({ type:'success', text: `Rejected user ${userId}` })
 setTimeout(() => setToastMessage(null), 4000)
 await refreshAccessRequests()
 } else {
 setToastMessage({ type:'error', text: data.error ||'Failed to reject user' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to reject user' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 }

 const purgeUser = async (userId: number) => {
 const ok = window.confirm(
 `Permanently delete user ${userId} and ALL their data? This cannot be undone.`
 )
 if (!ok) return

 setPurgingUserId(userId)
 try {
 const res = await fetch(`/api/admin/users/${userId}/purge`, {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify({ confirm: true })
 })
 const data = await res.json().catch(() => ({}))
 if (res.ok && data.success) {
 setToastMessage({ type:'success', text: `User ${userId} purged` })
 setTimeout(() => setToastMessage(null), 4000)
 // Refresh tables that reference users
 fetchAllData()
 } else {
 setToastMessage({ type:'error', text: data.error ||'Failed to purge user' })
 setTimeout(() => setToastMessage(null), 6000)
 }
 } catch {
 setToastMessage({ type:'error', text:'Failed to purge user' })
 setTimeout(() => setToastMessage(null), 6000)
 } finally {
 setPurgingUserId(null)
 }
 }
 
 const saveLlmConfig = async () => {
 setLlmSaving(true)
 setToastMessage(null)
 try {
 const response = await fetch('/api/admin/llm/config', {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 // Aliases go back untouched. They are what routes a purpose to a class,
 // so omitting them would silently unbind every caller from its pool.
 body: JSON.stringify({ version: 2, classes: llmConfig, aliases: llmAliases })
 })
 const data = await response.json()
 if (data.success) {
 // Render what came back rather than what was sent. The server is the
 // only thing that knows what it stored, and a save that quietly differs
 // from the request is the failure this whole screen was rebuilt over.
 setLlmConfig(data.classes || {})
 setLlmAliases(data.aliases || {})
 setLlmSource(data.source ||'database')
 setToastMessage({ type:'success', text: data.message ||'Model pools saved.' })
 setTimeout(() => setToastMessage(null), 5000)
 } else {
 setToastMessage({ type:'error', text: data.error ||'Failed to save model pools' })
 setTimeout(() => setToastMessage(null), 9000)
 }
 } catch (err) {
 setToastMessage({ type:'error', text:'Failed to save model pools' })
 setTimeout(() => setToastMessage(null), 6000)
 } finally {
 setLlmSaving(false)
 }
 }
 
 const testLlmModel = async (stage: string, index: number = 0) => {
 const modelEntry = (llmConfig[stage] || [])[index]
 if (!modelEntry) return
 
 const testKey = `${stage}-${index}`
 setLlmTesting(prev => ({ ...prev, [testKey]: true }))
 setLlmTestResult(prev => ({ ...prev, [testKey]: { success: false } }))
 
 try {
 const response = await fetch('/api/admin/llm/test', {
 method:'POST',
 credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify({
 provider: modelEntry.provider,
 model: modelEntry.model
 })
 })
 const data = await response.json()
 setLlmTestResult(prev => ({ ...prev, [testKey]: data }))
 } catch (err) {
 setLlmTestResult(prev => ({ 
 ...prev, 
 [testKey]: { success: false, error:'Request failed' }
 }))
 } finally {
 setLlmTesting(prev => ({ ...prev, [testKey]: false }))
 }
 }
 
 // Add a model to a class pool
 const addModelToStage = (stage: string, provider: string, model: string) => {
 setLlmConfig(prev => ({ ...prev, [stage]: [...(prev[stage] || []), { provider, model }] }))
 }
 
 // Remove a model from a class pool
 const removeModelFromStage = (stage: string, index: number) => {
 setLlmConfig(prev => {
 if ((prev[stage] || []).length <= 1) return prev // Keep at least one model
 return { ...prev, [stage]: prev[stage].filter((_, i) => i !== index) }
 })
 }
 
 // Move a model up in priority
 const moveModelUp = (stage: string, index: number) => {
 if (index <= 0) return
 setLlmConfig(prev => {
 const newModels = [...(prev[stage] || [])]
 const temp = newModels[index - 1]
 newModels[index - 1] = newModels[index]
 newModels[index] = temp
 return { ...prev, [stage]: newModels }
 })
 }
 
 // Move a model down in priority
 const moveModelDown = (stage: string, index: number) => {
 setLlmConfig(prev => {
 const models = prev[stage] || []
 if (index >= models.length - 1) return prev
 const newModels = [...models]
 const temp = newModels[index + 1]
 newModels[index + 1] = newModels[index]
 newModels[index] = temp
 return { ...prev, [stage]: newModels }
 })
 }

 /**
  * Pools in a fixed order, ascending, with anything unrecognised after.
  *
  * Object key order would otherwise decide it, which means the list silently
  * reorders itself the first time the server serialises the document
  * differently.
  */
 const orderedClasses = (): string[] => {
 const known = ['small','medium','large','reasoning']
 const present = Object.keys(llmConfig)
 return [
 ...known.filter(c => present.includes(c)),
 ...present.filter(c => !known.includes(c)).sort(),
 ]
 }

 /** Whether a pool can fall back off its primary provider. */
 const spansProviders = (entries: LLMModelEntry[]): boolean =>
 new Set(entries.map(e => e.provider)).size > 1

 /**
  * The named passes, in pipeline order, separated from the bare role aliases.
  *
  * A dotted key is a stage a caller announces as its purpose — `compass.fusion`
  * is one pass of one turn. A bare key like `router` is an older contract where
  * the caller names the alias as its class. They are both rows in `aliases` and
  * they are not the same thing to tune, so they are not shown together.
  *
  * Ordered as the turn runs rather than alphabetically, because the question
  * being asked here is "which pass produced this" and the answer arrives in
  * that order in the telemetry table beneath the chat.
  */
 const PASS_ORDER = [
'compass.front_door','compass.directions','compass.interpreter',
'compass.segmenter','compass.extractor','compass.fusion','compass.bounce','compass.recompose',
 ]
 /**
  * Stages grouped by the subsystem that calls them, Compass first.
  *
  * There are two dozen of these across five subsystems, and a flat
  * alphabetical list buries the chat pipeline — the part anyone opening this
  * tab came to adjust — between the journey engine and the motivator.
  */
 const stageGroups = (): { namespace: string; stages: string[] }[] => {
 const byNamespace = new Map<string, string[]>()
 for (const key of Object.keys(llmAliases)) {
 if (!key.includes('.')) continue
 const ns = key.slice(0, key.indexOf('.'))
 byNamespace.set(ns, [...(byNamespace.get(ns) || []), key])
 }
 const order = (ns: string, stages: string[]) =>
 ns ==='compass'
 ? [...PASS_ORDER.filter(s => stages.includes(s)),
 ...stages.filter(s => !PASS_ORDER.includes(s)).sort()]
 : [...stages].sort()
 return [...byNamespace.keys()]
 .sort((a, b) => (a ==='compass' ? -1 : b ==='compass' ? 1 : a.localeCompare(b)))
 .map(ns => ({ namespace: ns, stages: order(ns, byNamespace.get(ns)!) }))
 }
 const roleAliases = (): string[] =>
 Object.keys(llmAliases).filter(k => !k.includes('.')).sort()

 /** What a stage's class currently sends first. Empty when the class is gone. */
 const primaryFor = (className: string): LLMModelEntry | undefined =>
 (llmConfig[className] || [])[0]

 /**
  * Point one pass at a different class.
  *
  * Local only until Save, like every other edit on this tab. The gateway reads
  * the stage before the class the caller asked for, so this is the whole of
  * what it takes to move a pass — no deploy, and the caller keeps sending what
  * it always sent.
  */
 const setStageClass = (stage: string, className: string) => {
 setLlmAliases(prev => ({ ...prev, [stage]: className }))
 }
 
 // Get all available models as flat list for dropdown
 const getAllModels = (): { provider: string; model: string; displayName: string; price: string }[] => {
 const models: { provider: string; model: string; displayName: string; price: string }[] = []
 Object.entries(llmProviders).forEach(([provider, providerData]) => {
 providerData.models.forEach(model => {
 models.push({
 provider,
 model: model.id,
 displayName: `${providerData.name}: ${model.name}`,
 price: `$${model.input_price}/$${model.output_price}`
 })
 })
 })
 return models
 }
 
 // Check if a model is already in a class pool
 const isModelInStage = (stage: string, provider: string, model: string): boolean => {
 return (llmConfig[stage] || []).some(m => m.provider === provider && m.model === model)
 }
 
 // Get model display info
 const getModelInfo = (provider: string, modelId: string) => {
 const providerData = llmProviders[provider]
 if (!providerData) return { name: modelId, price:'N/A' }
 const model = providerData.models.find(m => m.id === modelId)
 if (!model) return { name: modelId, price:'N/A' }
 return { 
 name: model.name, 
 price: `$${model.input_price}/$${model.output_price}`,
 description: model.description
 }
 }
 
 // Start editing a model's prompt suffix
 const startEditingSuffix = (stage: string, index: number) => {
 const key = `${stage}-${index}`
 const model = (llmConfig[stage] || [])[index]
 if (!model) return
 setEditingSuffix(key)
 setSuffixDraft(model.prompt_suffix ||'')
 }
 
 // Save the edited prompt suffix
 const saveSuffix = (stage: string, index: number) => {
 setLlmConfig(prev => {
 const newModels = [...(prev[stage] || [])]
 newModels[index] = { ...newModels[index], prompt_suffix: suffixDraft || undefined }
 return { ...prev, [stage]: newModels }
 })
 setEditingSuffix(null)
 setSuffixDraft('')
 }
 
 // Cancel suffix editing
 const cancelSuffixEdit = () => {
 setEditingSuffix(null)
 setSuffixDraft('')
 }
 
 // Check if a model has a prompt suffix
 const hasSuffix = (entry: LLMModelEntry): boolean => {
 return Boolean(entry.prompt_suffix && entry.prompt_suffix.trim().length > 0)
 }
 
 const formatCost = (cost: number) => {
 if (cost < 0.01) return `$${cost.toFixed(6)}`
 if (cost < 1) return `$${cost.toFixed(4)}`
 return `$${cost.toFixed(2)}`
 }
 
 const formatNumber = (num: number) => {
 if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
 if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
 return num.toString()
 }
 
 const getProviderColor = (provider: string) => {
 const colors: Record<string, string> = {
'openai':'bg-ok/20 text-ok border-ok/30',
'groq':'bg-warn/20 text-warn border-warn/30',
'anthropic':'bg-brand/20 text-brand border-brand/30',
'deepseek':'bg-brand/20 text-brand border-brand/30'
 }
 return colors[provider.toLowerCase()] ||'bg-surface-alt/20 text-muted border-soft/30'
 }
 
 // Access denied view
 if (isAdmin === false) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-primary via-primary to-primary flex items-center justify-center p-4">
 <Card className="max-w-md bg-primary/50 border-bad/30">
 <CardHeader className="text-center">
 <div className="mx-auto w-16 h-16 rounded-full bg-bad/20 flex items-center justify-center mb-4">
 <Shield className="w-8 h-8 text-bad" />
 </div>
 <CardTitle className="text-bad">Access Denied</CardTitle>
 <CardDescription className="text-muted">
 You don't have permission to access the admin panel.
 This area is restricted to authorized administrators only.
 </CardDescription>
 </CardHeader>
 <CardContent>
 <Button 
 onClick={() => navigate(DEFAULT_APP_HOME)} 
 className="w-full bg-soft hover:bg-soft"
 >
 Return to Dashboard
 </Button>
 </CardContent>
 </Card>
 </div>
 )
 }
 
 // Loading state
 if (loading && isAdmin === null) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-primary via-primary to-primary flex items-center justify-center">
 <div className="text-center">
 <RefreshCw className="w-8 h-8 text-brand animate-spin mx-auto mb-4" />
 <p className="text-muted">Checking admin access...</p>
 </div>
 </div>
 )
 }
 
 return (
 <div className="min-h-screen bg-gradient-to-br from-primary via-primary to-primary">
 <div className="max-w-7xl mx-auto p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center">
 <Shield className="w-6 h-6 text-on-brand" />
 </div>
 <div>
 <h1 className="text-xl font-semibold text-primary">Admin Panel</h1>
 <p className="text-muted text-sm">AI Usage Analytics & System Monitoring</p>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <Button
 onClick={() => navigate('/admin/purge')}
 variant="outline"
 className="border-bad/40 bg-primary/50 hover:bg-bad/20 text-bad"
 >
 <Trash2 className="w-4 h-4 mr-2" />
 User cleanup
 </Button>
 <select 
 value={days}
 onChange={(e) => setDays(Number(e.target.value))}
 className="bg-primary border border-soft rounded-lg px-3 py-2 text-secondary text-sm"
 >
 <option value={7}>Last 7 days</option>
 <option value={30}>Last 30 days</option>
 <option value={90}>Last 90 days</option>
 <option value={365}>Last year</option>
 </select>
 <Button 
 onClick={fetchAllData} 
 variant="outline"
 className="border-soft bg-primary/50 hover:bg-soft"
 disabled={loading}
 >
 <RefreshCw className={`w-4 h-4 mr-2 ${loading ?'animate-spin' :''}`} />
 Refresh
 </Button>
 </div>
 </div>
 
 {error && (
 <div className="bg-bad/10 border border-bad/30 rounded-lg p-4 flex items-center gap-3">
 <AlertCircle className="w-5 h-5 text-bad" />
 <span className="text-bad">{error}</span>
 </div>
 )}
 
 {/* Overview Stats */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
 <Card className="bg-primary/50 border-soft/50">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-muted text-sm">Total Requests</p>
 <p className="text-xl font-semibold text-primary">
 {formatNumber(overview?.total.requests || 0)}
 </p>
 </div>
 <Activity className="w-8 h-8 text-brand opacity-50" />
 </div>
 </CardContent>
 </Card>
 
 <Card className="bg-primary/50 border-soft/50">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-muted text-sm">Input Tokens</p>
 <p className="text-xl font-semibold text-primary">
 {formatNumber(overview?.total.input_tokens || 0)}
 </p>
 </div>
 <TrendingUp className="w-8 h-8 text-ok opacity-50" />
 </div>
 </CardContent>
 </Card>
 
 <Card className="bg-primary/50 border-soft/50">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-muted text-sm">Output Tokens</p>
 <p className="text-xl font-semibold text-primary">
 {formatNumber(overview?.total.output_tokens || 0)}
 </p>
 </div>
 <BarChart3 className="w-8 h-8 text-brand opacity-50" />
 </div>
 </CardContent>
 </Card>
 
 <Card className="bg-primary/50 border-soft/50">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-muted text-sm">Total Cost</p>
 <p className="text-xl font-semibold text-warn">
 {formatCost(overview?.total.cost || 0)}
 </p>
 </div>
 <DollarSign className="w-8 h-8 text-warn opacity-50" />
 </div>
 </CardContent>
 </Card>
 
 <Card className="bg-primary/50 border-soft/50">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-muted text-sm">Active Users</p>
 <p className="text-xl font-semibold text-primary">
 {systemStats?.active_users_30d || 0}
 <span className="text-muted text-sm ml-1">/ {systemStats?.total_users || 0}</span>
 </p>
 </div>
 <Users className="w-8 h-8 text-brand opacity-50" />
 </div>
 </CardContent>
 </Card>
 </div>
 
 {/* Main Content Tabs */}
 {/* AI Spend first: Providers / User Usage / Recent Requests used to read a
     Mongo table that is no longer written, so they looked empty after the wipe
     even when the gateway ledger had turns. They now read the same ledger; this
     tab is still the one that shows per-turn averages and provider failover. */}
 <Tabs defaultValue="ai-spend" className="space-y-4">
 <TabsList className="bg-primary/50 border border-soft/50">
 <TabsTrigger value="ai-spend" className="data-[state=active]:bg-soft">
 <DollarSign className="w-4 h-4 mr-2" />
 AI Spend
 </TabsTrigger>
 <TabsTrigger value="providers" className="data-[state=active]:bg-soft">
 <Cpu className="w-4 h-4 mr-2" />
 Providers & Models
 </TabsTrigger>
 <TabsTrigger value="users" className="data-[state=active]:bg-soft">
 <Users className="w-4 h-4 mr-2" />
 User Usage
 </TabsTrigger>
 <TabsTrigger value="requests" className="data-[state=active]:bg-soft">
 <Activity className="w-4 h-4 mr-2" />
 Recent Requests
 </TabsTrigger>
 <TabsTrigger value="access" className="data-[state=active]:bg-soft">
 <Shield className="w-4 h-4 mr-2" />
 Access Requests
 </TabsTrigger>
 <TabsTrigger value="llm-config" className="data-[state=active]:bg-soft">
 <Settings className="w-4 h-4 mr-2" />
 LLM Config
 </TabsTrigger>
 <TabsTrigger value="feedback" className="data-[state=active]:bg-soft">
 <MessageSquarePlus className="w-4 h-4 mr-2" />
 Feedback
 </TabsTrigger>
 </TabsList>
 
 {/* Providers Tab */}
 <TabsContent value="providers" className="space-y-4">
 {/* By Provider */}
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">Usage by Provider</CardTitle>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">Provider</TableHead>
 <TableHead className="text-muted text-right">Requests</TableHead>
 <TableHead className="text-muted text-right">Input Tokens</TableHead>
 <TableHead className="text-muted text-right">Output Tokens</TableHead>
 <TableHead className="text-muted text-right">Cost</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {overview?.by_provider.map((provider) => (
 <TableRow key={provider.provider} className="border-soft/50">
 <TableCell>
 <Badge className={getProviderColor(provider.provider)}>
 {provider.provider}
 </Badge>
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(provider.requests)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(provider.input_tokens)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(provider.output_tokens)}
 </TableCell>
 <TableCell className="text-right text-warn font-medium">
 {formatCost(provider.cost)}
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 
 {/* By Agent/Service */}
 {overview?.by_agent && overview.by_agent.length > 0 && (
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">Usage by Service</CardTitle>
 <CardDescription className="text-muted">
 Breakdown by which {BRAND.assistant} service generated the cost
 </CardDescription>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">Service</TableHead>
 <TableHead className="text-muted text-right">Requests</TableHead>
 <TableHead className="text-muted text-right">Input Tokens</TableHead>
 <TableHead className="text-muted text-right">Output Tokens</TableHead>
 <TableHead className="text-muted text-right">Cost</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {overview.by_agent.map((agent) => {
 const agentColors: Record<string, string> = {
'horton_chat_v5':'bg-brand/20 text-brand border-brand/30',
'journey_engine':'bg-brand/20 text-brand border-brand/30',
'motivator':'bg-bad/20 text-bad border-bad/30',
 }
 const color = agentColors[agent.agent] ||'bg-surface-alt/20 text-muted border-soft/30'
 return (
 <TableRow key={agent.agent} className="border-soft/50">
 <TableCell>
 <Badge className={color}>
 {agent.agent}
 </Badge>
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(agent.requests)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(agent.input_tokens)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(agent.output_tokens)}
 </TableCell>
 <TableCell className="text-right text-warn font-medium">
 {formatCost(agent.cost)}
 </TableCell>
 </TableRow>
 )
 })}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 )}

 {/* By Model */}
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">Usage by Model</CardTitle>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">Provider</TableHead>
 <TableHead className="text-muted">Model</TableHead>
 <TableHead className="text-muted text-right">Requests</TableHead>
 <TableHead className="text-muted text-right">Input</TableHead>
 <TableHead className="text-muted text-right">Output</TableHead>
 <TableHead className="text-muted text-right">Avg Response</TableHead>
 <TableHead className="text-muted text-right">Cost</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {overview?.by_model.map((model, idx) => (
 <TableRow key={`${model.provider}-${model.model}-${idx}`} className="border-soft/50">
 <TableCell>
 <Badge className={getProviderColor(model.provider)}>
 {model.provider}
 </Badge>
 </TableCell>
 <TableCell className="text-secondary font-mono text-sm">
 {model.model}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(model.requests)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(model.input_tokens)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(model.output_tokens)}
 </TableCell>
 <TableCell className="text-right text-muted">
 {model.avg_response_time_ms ? `${model.avg_response_time_ms}ms` :'-'}
 </TableCell>
 <TableCell className="text-right text-warn font-medium">
 {formatCost(model.cost)}
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 </TabsContent>
 
 {/* Users Tab */}
 <TabsContent value="users">
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">AI Usage by User</CardTitle>
 <CardDescription className="text-muted">
 From the gateway ledger (same source as AI Spend). Sorted by total cost.
 </CardDescription>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">User</TableHead>
 <TableHead className="text-muted">Email</TableHead>
 <TableHead className="text-muted text-right">Requests</TableHead>
 <TableHead className="text-muted text-right">Total Tokens</TableHead>
 <TableHead className="text-muted text-right">Cost</TableHead>
 <TableHead className="text-muted text-right">Last Used</TableHead>
 <TableHead className="text-muted text-right">Admin</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {userUsage.map((user) => (
 <TableRow key={user.user_id} className="border-soft/50">
 <TableCell className="text-primary font-medium">
 {user.username}
 </TableCell>
 <TableCell className="text-muted">
 {user.email}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(user.requests)}
 </TableCell>
 <TableCell className="text-right text-secondary">
 {formatNumber(user.total_tokens)}
 </TableCell>
 <TableCell className="text-right text-warn font-medium">
 {formatCost(user.cost)}
 </TableCell>
 <TableCell className="text-right text-muted text-sm">
 {user.last_used 
 ? new Date(user.last_used).toLocaleDateString() 
 :'-'}
 </TableCell>
<TableCell className="text-right">
{/*
  Purge deletes a saas-core ACCOUNT, so it needs one to exist. A row whose
  spend carries no saas id reports `user_id: 0`, and the button posted to
  /users/0/purge and reported success against a user that was never there —
  which reads as "the button does not work", and was how it was found.

  These rows are real spend with no account behind them: preflight probes,
  anything run as `system`, and every synthetic user a test drives through
  the compat router. They are cleared with deploy/clear-ai-spend.sh.
*/}
{user.user_id > 0 ? (
<Button
variant="outline"
className="border-bad/50 text-bad hover:text-bad hover:bg-bad/20"
onClick={() => purgeUser(user.user_id)}
disabled={purgingUserId === user.user_id}
>
<Trash2 className={`w-4 h-4 mr-2 ${purgingUserId === user.user_id ?'animate-pulse' :''}`} />
Purge
</Button>
) : (
<span
className="text-muted text-sm"
title="Spend with no saas-core account behind it — a probe, a system call, or a test user. Clear it with deploy/clear-ai-spend.sh."
>
no account
</span>
)}
</TableCell>
 </TableRow>
 ))}
 {userUsage.length === 0 && (
 <TableRow>
 <TableCell colSpan={7} className="text-center text-muted py-8">
 No user activity in this period
 </TableCell>
 </TableRow>
 )}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 </TabsContent>
 
 {/* Recent Requests Tab */}
 <TabsContent value="requests">
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">Recent AI Requests</CardTitle>
 <CardDescription className="text-muted">
 Last 100 requests across all users
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">Time</TableHead>
 <TableHead className="text-muted">User</TableHead>
 <TableHead className="text-muted">Provider</TableHead>
 <TableHead className="text-muted">Model</TableHead>
 <TableHead className="text-muted">Operation</TableHead>
 <TableHead className="text-muted text-right">In/Out</TableHead>
 <TableHead className="text-muted text-right">Cost</TableHead>
 <TableHead className="text-muted text-right">Time</TableHead>
 <TableHead className="text-muted text-center">Status</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {recentRequests.map((req) => (
 <TableRow key={req.id} className="border-soft/50">
 <TableCell className="text-muted text-sm whitespace-nowrap">
 {req.timestamp 
 ? new Date(req.timestamp).toLocaleString() 
 :'-'}
 </TableCell>
 <TableCell className="text-secondary">
 {req.user.username}
 </TableCell>
 <TableCell>
 <Badge className={getProviderColor(req.provider)}>
 {req.provider}
 </Badge>
 </TableCell>
 <TableCell className="text-secondary font-mono text-xs">
 {req.model}
 </TableCell>
 <TableCell className="text-muted text-sm">
 {req.operation || req.agent ||'-'}
 </TableCell>
 <TableCell className="text-right text-secondary text-sm">
 <span className="text-ok">{formatNumber(req.input_tokens)}</span>
 <span className="text-muted">/</span>
 <span className="text-brand">{formatNumber(req.output_tokens)}</span>
 </TableCell>
 <TableCell className="text-right text-warn text-sm">
 {formatCost(req.cost)}
 </TableCell>
 <TableCell className="text-right text-muted text-sm">
 {req.response_time_ms ? `${req.response_time_ms}ms` :'-'}
 </TableCell>
 <TableCell className="text-center">
 {req.success ? (
 <CheckCircle2 className="w-4 h-4 text-ok mx-auto" />
 ) : (
 <XCircle className="w-4 h-4 text-bad mx-auto" />
 )}
 </TableCell>
 </TableRow>
 ))}
 {recentRequests.length === 0 && (
 <TableRow>
 <TableCell colSpan={9} className="text-center text-muted py-8">
 No requests found
 </TableCell>
 </TableRow>
 )}
 </TableBody>
 </Table>
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 {/* Access Requests Tab */}
 <TabsContent value="access">
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="text-lg text-primary">Pending Access Requests</CardTitle>
 <CardDescription className="text-muted">
 Users who registered but need admin approval to sign in
 </CardDescription>
 </div>
 <Button
 variant="outline"
 className="border-soft/50"
 onClick={refreshAccessRequests}
 disabled={accessLoading}
 >
 <RefreshCw className={`w-4 h-4 mr-2 ${accessLoading ?'animate-spin' :''}`} />
 Refresh
 </Button>
 </div>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50">
 <TableHead className="text-muted">ID</TableHead>
 <TableHead className="text-muted">Username</TableHead>
 <TableHead className="text-muted">Email</TableHead>
 <TableHead className="text-muted">Created</TableHead>
 <TableHead className="text-muted text-right">Actions</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {accessRequests.map((r) => (
 <TableRow key={r.id} className="border-soft/50">
 <TableCell className="text-secondary">{r.id}</TableCell>
 <TableCell className="text-primary font-medium">{r.username}</TableCell>
 <TableCell className="text-muted">{r.email}</TableCell>
 <TableCell className="text-muted text-sm">
 {r.created_at ? new Date(r.created_at).toLocaleString() :'-'}
 </TableCell>
 <TableCell className="text-right">
 <div className="flex justify-end gap-2">
 <Button
 className="bg-ok hover:bg-ok text-on-brand"
 onClick={() => approveAccess(r.id)}
 >
 <CheckCircle2 className="w-4 h-4 mr-2" />
 Approve
 </Button>
 <Button
 variant="outline"
 className="border-bad/50 text-bad hover:text-bad hover:bg-bad/20"
 onClick={() => rejectAccess(r.id)}
 >
 <XCircle className="w-4 h-4 mr-2" />
 Reject
 </Button>
 </div>
 </TableCell>
 </TableRow>
 ))}
 {accessRequests.length === 0 && (
 <TableRow>
 <TableCell colSpan={5} className="text-center text-muted py-8">
 No pending access requests
 </TableCell>
 </TableRow>
 )}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 </TabsContent>
 
 {/* LLM Configuration Tab */}
 <TabsContent value="llm-config" className="space-y-6">
 {/* Per-pass routing. Above the pools because this is the knob you reach
 for when one pass is producing bad output — the pools are what a class
 is made of, this is which class a pass gets. */}
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary flex items-center gap-2">
 <Settings className="w-5 h-5 text-brand" />
 Passes
 </CardTitle>
 <CardDescription className="text-muted">
 Each pass of a turn, in the order it runs, and the class serving it. Move one pass to a
 different class to see what it does to that pass's output — the others are untouched.
 Every call is recorded against its pass name, so AI Spend and the telemetry under each
 chat show the effect.
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-6">
 {stageGroups().length === 0 && (
 <p className="text-sm text-muted">No named passes returned by the gateway.</p>
 )}
 {stageGroups().map(({ namespace, stages }) => (
 <div key={namespace} className="space-y-2">
 <h3 className="text-primary font-medium">
 {NAMESPACE_LABELS[namespace]?.name ?? namespace}
 <span className="ml-2 text-app-micro text-muted font-normal align-middle">
 {NAMESPACE_LABELS[namespace]?.desc ?? `${stages.length} passes`}
 </span>
 </h3>
 {stages.map((stage) => {
 const className = llmAliases[stage]
 const primary = primaryFor(className)
 return (
 <div
 key={stage}
 className="flex items-center gap-3 p-3 rounded-lg border border-soft/50 bg-primary/50"
 >
 <div className="flex-1 min-w-0">
 <span className="font-mono text-sm text-primary">{stage}</span>
 {PASS_NOTES[stage] && (
 <p className="text-app-micro text-muted mt-0.5">{PASS_NOTES[stage]}</p>
 )}
 </div>
 <div className="text-right shrink-0">
 {primary ? (
 <>
 <Badge className={getProviderColor(primary.provider)}>{primary.provider}</Badge>
 <span className="ml-2 text-sm text-primary font-medium">
 {getModelInfo(primary.provider, primary.model).name}
 </span>
 </>
 ) : (
 // A class with no pool cannot serve, and the gateway will fall
 // back to whatever the caller asked for. Say so rather than
 // showing an empty cell that reads as "fine".
 <span className="text-sm text-warn">
 class “{className}” has no models — this pass ignores the setting
 </span>
 )}
 </div>
 <select
 value={className}
 onChange={(e) => setStageClass(stage, e.target.value)}
 className="bg-primary border border-strong rounded-md px-3 py-2 text-primary text-sm shrink-0 focus:border-brand focus:ring-1 focus:ring-brand"
 >
 {orderedClasses().map(c => (
 <option key={c} value={c}>{c}</option>
 ))}
 {/* The saved value survives being absent from the pools, so the
 select shows it rather than silently snapping to the first
 option and rewriting the config on the next save. */}
 {!orderedClasses().includes(className) && (
 <option value={className}>{className} (missing)</option>
 )}
 </select>
 </div>
 )
 })}
 </div>
 ))}
 {roleAliases().length > 0 && (
 <p className="text-app-micro text-muted pt-2">
 Also routed by name, by callers that ask for a role instead of a size:{' '}
 <span className="font-mono">
 {roleAliases().map(a => `${a} → ${llmAliases[a]}`).join(',')}
 </span>
 </p>
 )}
 </CardContent>
 </Card>

 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary flex items-center gap-2">
 <Settings className="w-5 h-5 text-brand" />
 Model Classes (Failover Order)
 </CardTitle>
 <CardDescription className="text-muted">
 Callers ask for a class, never a model. The first entry serves; if it fails, the gateway
 walks down the list. Use ↑↓ to reorder. Saved changes reach the gateway within 15 seconds.
 </CardDescription>
 {llmSource ==='defaults' && (
 <div className="mt-2 text-xs text-warn bg-warn/10 border border-warn/30 rounded px-3 py-2">
 Nothing has been saved yet — these are the pools shipped in code. Saving here
 takes over from them.
 </div>
 )}
 </CardHeader>
 <CardContent className="space-y-6">
 {Object.keys(llmConfig).length === 0 && (
 <p className="text-sm text-muted">No model classes returned by the gateway.</p>
 )}
 {orderedClasses().map((stage) => {
 const stageModels = llmConfig[stage] || []
 const stageLabels: Record<string, { name: string; desc: string; color: string }> = {
 small: { name:'🚦 Small', desc:'Closed-set classification. The front door and the bounce pass.', color:'text-brand' },
 medium: { name:'⚙️ Medium', desc:'Structured work that is not a judgement call — reconciling proposals against the life map.', color:'text-brand' },
 large: { name:'🧠 Large', desc:'Interpretation, extraction and naming, where being wrong reaches the reply.', color:'text-brand' },
 reasoning: { name:'🔬 Reasoning', desc:'Reserved for work that should think before answering.', color:'text-ok' }
 }
 const stageInfo = stageLabels[stage] ?? {
 name: stage, desc:'Custom class.', color:'text-primary'
 }
 // Derived from the aliases being edited, not from what the server last
 // said, so moving a pass in the card above updates this line immediately.
 // Two views of one mapping that disagree until Save is how you talk
 // yourself into believing a change landed.
 const users = Object.keys(llmAliases).filter(p => llmAliases[p] === stage).sort()
 const required = llmRequired.includes(stage)

 return (
 <div key={stage} className="p-4 bg-primary/50 rounded-lg border border-soft/50">
 <div className="flex items-start justify-between mb-4">
 <div>
 <h3 className={`text-primary font-medium text-lg ${stageInfo.color}`}>
 {stageInfo.name}
 {required && (
 <span className="ml-2 text-app-micro text-muted font-normal align-middle">
 required — callers name it directly
 </span>
 )}
 </h3>
 <p className="text-sm text-muted mt-1">{stageInfo.desc}</p>
 {users.length > 0 && (
 // The blast radius of a reorder. Presenting pools as independent
 // "phases" hid that one pool answers for many callers at once.
 <p className="text-app-micro text-muted mt-1">
 Answers for: <span className="font-mono">{users.join(', ')}</span>
 </p>
 )}
 {stageModels.length > 0 && !spansProviders(stageModels) && (
 <p className="text-app-micro text-warn mt-1">
 Only {stageModels[0].provider} — an outage there has nowhere to fall back to.
 </p>
 )}
 </div>
 </div>
 
 {/* Priority-ordered model list */}
 <div className="space-y-2 mb-4">
 {stageModels.map((entry, index) => {
 const modelInfo = getModelInfo(entry.provider, entry.model)
 const testKey = `${stage}-${index}`
 const testResult = llmTestResult[testKey]
 const isTesting = llmTesting[testKey]
 
 return (
 <div key={`${entry.provider}-${entry.model}-${index}`} className="space-y-2">
 <div 
 className={`flex items-center gap-3 p-3 rounded-lg border ${
 index === 0 
 ?'bg-gradient-to-r from-ok/20 to-primary/50 border-ok/50' 
 :'bg-primary/50 border-soft/50'
 }`}
 >
 {/* Priority badge */}
 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
 index === 0 
 ?'bg-ok text-on-brand' 
 :'bg-soft text-secondary'
 }`}>
 {index + 1}
 </div>
 
 {/* Model info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <Badge className={getProviderColor(entry.provider)}>
 {entry.provider}
 </Badge>
 <span className="text-primary font-medium truncate">{modelInfo.name}</span>
 <span className="text-muted text-xs">{modelInfo.price}/MTok</span>
 {hasSuffix(entry) && (
 <Badge className="bg-warn/20 text-warn text-app-micro px-1.5">
 📝 suffix
 </Badge>
 )}
 </div>
 <div className="flex items-center gap-2">
 {index === 0 && (
 <span className="text-xs text-ok">Primary model</span>
 )}
 <button
 onClick={() => startEditingSuffix(stage, index)}
 className="text-xs text-muted hover:text-brand underline"
 >
 {hasSuffix(entry) ?'Edit prompt suffix' :'Add prompt suffix'}
 </button>
 </div>
 {/* Suffix Preview */}
 {hasSuffix(entry) && editingSuffix !== testKey && (
 <div className="mt-1 text-xs text-muted font-mono bg-primary/50 px-2 py-1 rounded max-w-[400px] truncate" title={entry.prompt_suffix}>
 {entry.prompt_suffix!.split('\n')[0].substring(0, 60)}...
 </div>
 )}
 </div>
 
 {/* Actions */}
 <div className="flex items-center gap-1">
 {/* Move up */}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => moveModelUp(stage, index)}
 disabled={index === 0}
 className="h-8 w-8 p-0 text-muted hover:text-primary hover:bg-soft"
 >
 <ChevronUp className="w-4 h-4" />
 </Button>
 
 {/* Move down */}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => moveModelDown(stage, index)}
 disabled={index === stageModels.length - 1}
 className="h-8 w-8 p-0 text-muted hover:text-primary hover:bg-soft"
 >
 <ChevronDown className="w-4 h-4" />
 </Button>
 
 {/* Test */}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => testLlmModel(stage, index)}
 disabled={isTesting}
 className="h-8 w-8 p-0 text-muted hover:text-warn hover:bg-soft"
 >
 {isTesting ? (
 <RefreshCw className="w-4 h-4 animate-spin" />
 ) : (
 <PlayCircle className="w-4 h-4" />
 )}
 </Button>
 
 {/* Remove */}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => removeModelFromStage(stage, index)}
 disabled={stageModels.length <= 1}
 className="h-8 w-8 p-0 text-muted hover:text-bad hover:bg-soft"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 
 {/* Test result indicator */}
 {testResult && (
 <div className={`ml-2 ${testResult.success ?'text-ok' :'text-bad'}`}>
 {testResult.success ? (
 <CheckCircle2 className="w-5 h-5" />
 ) : (
 <XCircle className="w-5 h-5" />
 )}
 </div>
 )}
 </div>
 
 {/* Prompt Suffix Editor */}
 {editingSuffix === testKey && (
 <div className="mt-2 p-3 bg-primary rounded-lg border border-brand/50">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-brand font-medium">
 📝 Model-Specific Prompt Suffix
 </span>
 <span className="text-xs text-muted">
 Appended to base system prompt for this model only
 </span>
 </div>
 <textarea
 value={suffixDraft}
 onChange={(e) => setSuffixDraft(e.target.value)}
 placeholder="e.g., CRITICAL: Output ONLY valid JSON. DO NOT use <think> tags..."
 className="w-full h-24 px-3 py-2 bg-primary border border-strong rounded text-sm text-secondary font-mono focus:border-brand focus:ring-1 focus:ring-brand resize-none"
 />
 <div className="flex justify-end gap-2 mt-2">
 <Button
 size="sm"
 variant="ghost"
 onClick={cancelSuffixEdit}
 className="text-muted hover:text-primary"
 >
 Cancel
 </Button>
 <Button
 size="sm"
 variant="default"
 onClick={() => saveSuffix(stage, index)}
 className="bg-brand hover:bg-brand"
 >
 Save Suffix
 </Button>
 </div>
 <p className="text-xs text-muted mt-2">
 💡 Tip: Use this to fix model-specific behaviors like forcing JSON output, 
 suppressing &lt;think&gt; tags, or adjusting output format.
 </p>
 </div>
 )}
 </div>
 )
 })}
 </div>
 
 {/* Add model dropdown */}
 <div className="flex gap-2">
 <select
 id={`add-model-${stage}`}
 className="flex-1 px-3 py-2 bg-primary border border-strong rounded-md text-primary text-sm focus:border-brand focus:ring-1 focus:ring-brand"
 defaultValue=""
 >
 <option value="" disabled>Add a failover model...</option>
 {getAllModels()
 .filter(m => !isModelInStage(stage, m.provider, m.model))
 .map(m => (
 <option key={`${m.provider}-${m.model}`} value={`${m.provider}|${m.model}`}>
 {m.displayName} ({m.price})
 </option>
 ))
 }
 </select>
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 const select = document.getElementById(`add-model-${stage}`) as HTMLSelectElement
 if (select?.value) {
 const [provider, model] = select.value.split('|')
 addModelToStage(stage, provider, model)
 select.value =''
 }
 }}
 className="border-strong hover:bg-soft"
 >
 <Plus className="w-4 h-4 mr-1" /> Add
 </Button>
 </div>
 </div>
 )
 })}
 
 {/* Save Button */}
 <div className="flex justify-end pt-4 border-t border-soft/50">
 <Button
 onClick={saveLlmConfig}
 disabled={llmSaving}
 className="bg-brand hover:bg-brand"
 >
 {llmSaving ? (
 <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <CheckCircle2 className="w-4 h-4 mr-2" />
 )}
 Save Configuration
 </Button>
 </div>
 </CardContent>
 </Card>
 
 {/* Available Models Reference */}
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <CardTitle className="text-lg text-primary">Available Models Reference</CardTitle>
 <CardDescription className="text-muted">
 All available models with their specifications and pricing
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {Object.entries(llmProviders).map(([providerId, provider]) => (
 <div key={providerId} className="space-y-2">
 <h4 className="text-primary font-medium">{provider.name}</h4>
 <Table>
 <TableHeader>
 <TableRow className="border-soft/50 hover:bg-primary/30">
 <TableHead className="text-muted">Model</TableHead>
 <TableHead className="text-muted">Description</TableHead>
 <TableHead className="text-muted">Max Tokens</TableHead>
 <TableHead className="text-muted text-right">Input $/1M</TableHead>
 <TableHead className="text-muted text-right">Output $/1M</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {provider.models.map((model) => (
 <TableRow key={model.id} className="border-soft/50 hover:bg-primary/30">
 <TableCell className="text-primary font-mono text-sm">{model.id}</TableCell>
 <TableCell className="text-secondary">{model.description}</TableCell>
 <TableCell className="text-secondary">{model.max_tokens.toLocaleString()}</TableCell>
 <TableCell className="text-secondary text-right">${model.input_price.toFixed(2)}</TableCell>
 <TableCell className="text-secondary text-right">${model.output_price.toFixed(2)}</TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 {/* AI Spend Tab — reads the gateway's llm_calls ledger, which is a different
 and more trustworthy source than the cost columns on the Providers and User
 Usage tabs above. Those read ai_cost_records, which was fed a stage name
 where the model belonged and priced everything at a Claude Sonnet default. */}
 <TabsContent value="ai-spend">
 <AiSpendTab />
 </TabsContent>

 {/* Feedback Tab */}
 <TabsContent value="feedback">
 <Card className="bg-primary/50 border-soft/50">
 <CardHeader>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="text-lg text-primary flex items-center gap-2">
 <MessageSquarePlus className="w-5 h-5 text-brand" />
 User Feedback
 </CardTitle>
 <CardDescription className="text-muted">
 Bugs, suggestions, and feedback reported by users
 </CardDescription>
 </div>
 <div className="flex items-center gap-3">
 <select
 value={feedbackFilter}
 onChange={e => { setFeedbackFilter(e.target.value); fetchFeedback(e.target.value) }}
 className="bg-primary border border-soft rounded-lg px-3 py-2 text-secondary text-sm"
 >
 <option value="all">All statuses</option>
 <option value="new">New</option>
 <option value="reviewed">Reviewed</option>
 <option value="in_progress">In Progress</option>
 <option value="resolved">Resolved</option>
 <option value="dismissed">Dismissed</option>
 </select>
 <Button
 variant="outline"
 className="border-soft/50"
 onClick={() => fetchFeedback(feedbackFilter)}
 disabled={feedbackLoading}
 >
 <RefreshCw className={`w-4 h-4 mr-2 ${feedbackLoading ?'animate-spin' :''}`} />
 Refresh
 </Button>
 <Button
 variant="outline"
 className="border-soft/50 text-brand hover:bg-brand/10"
 onClick={archiveResolved}
 disabled={archiving || feedbackLoading}
 >
 <Archive className={`w-4 h-4 mr-2 ${archiving ?'animate-pulse' :''}`} />
 {archiving ?'Archiving...' :'Archive Resolved'}
 </Button>
 </div>
 </div>
 </CardHeader>
 <CardContent>
 {feedbackLoading && feedbackItems.length === 0 ? (
 <div className="text-center py-12 text-muted">
 <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
 Loading feedback...
 </div>
 ) : feedbackItems.length === 0 ? (
 <div className="text-center py-12 text-muted">
 No feedback found
 </div>
 ) : (
 <div className="space-y-3">
 {feedbackItems.map(item => {
 const fid = item.id ?? item._id ?? 0
 const isExpanded = expandedFeedback.has(fid)
 const categoryIcon = item.category ==='bug' ? <Bug className="w-4 h-4" />
 : item.category ==='suggestion' ? <Lightbulb className="w-4 h-4" />
 : <CircleDot className="w-4 h-4" />
 const categoryColor = item.category ==='bug' ?'bg-bad/20 text-bad border-bad/30'
 : item.category ==='suggestion' ?'bg-brand/20 text-brand border-brand/30'
 : item.category ==='expectation_mismatch' ?'bg-warn/20 text-warn border-warn/30'
 :'bg-surface-alt/20 text-muted border-soft/30'
 const statusColor = item.status ==='new' ?'bg-brand/20 text-brand border-brand/30'
 : item.status ==='reviewed' ?'bg-brand/20 text-brand border-brand/30'
 : item.status ==='in_progress' ?'bg-warn/20 text-warn border-warn/30'
 : item.status ==='resolved' ?'bg-ok/20 text-ok border-ok/30'
 :'bg-surface-alt/20 text-muted border-soft/30'
 const severityColor = item.severity ==='major' ?'text-bad'
 : item.severity ==='moderate' ?'text-warn'
 :'text-brand'

 return (
 <div key={fid} className="border border-soft/50 rounded-lg bg-primary/30 overflow-hidden">
 <div
 className="flex items-center gap-3 p-4 cursor-pointer hover:bg-primary/50 transition-colors"
 onClick={() => toggleFeedbackExpand(fid)}
 >
 <Badge className={categoryColor}>
 <span className="flex items-center gap-1">{categoryIcon} {item.category.replace('_','')}</span>
 </Badge>
 <Badge className={statusColor}>{item.status}</Badge>
 {item.severity && (
 <span className={`text-xs font-medium ${severityColor}`}>{item.severity}</span>
 )}
 <span className="flex-1 text-secondary text-sm truncate">{item.expected}</span>
 <span className="text-muted text-xs whitespace-nowrap">
 {item.context?.page_title || item.context?.page_route ||''}
 </span>
 <span className="text-muted text-xs whitespace-nowrap">
 {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() :''}
 </span>
 <ChevronDown className={`w-4 h-4 text-muted transition-transform ${isExpanded ?'rotate-180' :''}`} />
 </div>

 {isExpanded && (
 <div className="border-t border-soft/50 p-4 space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">What they expected</h4>
 <p className="text-sm text-secondary">{item.expected}</p>
 </div>
 {item.actual && (
 <div>
 <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">What actually happened</h4>
 <p className="text-sm text-secondary">{item.actual}</p>
 </div>
 )}
 {item.improvement && (
 <div className="md:col-span-2">
 <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">How to improve</h4>
 <p className="text-sm text-secondary">{item.improvement}</p>
 </div>
 )}
 </div>

 {item.context && (
 <div className="p-3 rounded-lg bg-primary/50 border border-soft/30">
 <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Context</h4>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
 <div><span className="text-muted">Page:</span> <span className="text-secondary">{item.context.page_title ||'-'}</span></div>
 <div><span className="text-muted">Route:</span> <span className="text-secondary font-mono">{item.context.page_route ||'-'}</span></div>
 <div><span className="text-muted">User ID:</span> <span className="text-secondary">{item.user_id}</span></div>
 <div><span className="text-muted">Mobile:</span> <span className="text-secondary">{item.context.is_mobile ?'Yes' :'No'}</span></div>
 <div><span className="text-muted">Viewport:</span> <span className="text-secondary">{item.context.viewport ||'-'}</span></div>
 <div><span className="text-muted">Submitted:</span> <span className="text-secondary">{item.submitted_at ? new Date(item.submitted_at).toLocaleString() :'-'}</span></div>
 {item.context.page_context && Object.entries(item.context.page_context).map(([k, v]) => (
 v != null && (
 <div key={k}><span className="text-muted">{k}:</span> <span className="text-secondary">{String(v)}</span></div>
 )
 ))}
 </div>
 </div>
 )}

 <div className="flex items-center gap-2 pt-2 border-t border-soft/30">
 <span className="text-xs text-muted mr-2">Set status:</span>
 {['new','reviewed','in_progress','resolved','dismissed'].map(s => (
 <Button
 key={s}
 size="sm"
 variant={item.status === s ?'default' :'outline'}
 className={item.status === s
 ?'bg-brand text-on-brand text-xs'
 :'border-strong text-muted text-xs hover:bg-soft'}
 onClick={e => { e.stopPropagation(); updateFeedbackStatus(fid, s) }}
 >
 {s.replace('_','')}
 </Button>
 ))}
 <div className="flex-1" />
 <Button
 size="sm"
 variant="outline"
 className="border-bad/30 text-bad text-xs hover:bg-bad/10"
 onClick={e => { e.stopPropagation(); deleteFeedback(fid) }}
 >
 <Trash2 className="w-3 h-3 mr-1" />
 Delete
 </Button>
 </div>
 </div>
 )}
 </div>
 )
 })}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Archived Feedback Section */}
 <Card className="bg-primary/50 border-soft/50 mt-4">
 <CardHeader className="cursor-pointer" onClick={() => { setShowArchived(v => !v); if (!showArchived && archivedItems.length === 0) fetchArchived() }}>
 <div className="flex items-center justify-between">
 <CardTitle className="text-base text-secondary flex items-center gap-2">
 <Archive className="w-4 h-4 text-muted" />
 Archived Feedback
 {archivedItems.length > 0 && <Badge className="bg-surface-alt/30 text-muted border-soft/30 ml-1">{archivedItems.length}</Badge>}
 </CardTitle>
 <ChevronDown className={`w-4 h-4 text-muted transition-transform ${showArchived ?'rotate-180' :''}`} />
 </div>
 </CardHeader>
 {showArchived && (
 <CardContent>
 {archivedLoading ? (
 <div className="text-center py-8 text-muted">
 <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
 Loading archived feedback...
 </div>
 ) : archivedItems.length === 0 ? (
 <div className="text-center py-8 text-muted text-sm">No archived feedback yet</div>
 ) : (
 <div className="space-y-2">
 {archivedItems.map((item, idx) => {
 const fid = item.id ?? item._id ?? idx
 const categoryIcon = item.category ==='bug' ? <Bug className="w-3.5 h-3.5" />
 : item.category ==='suggestion' ? <Lightbulb className="w-3.5 h-3.5" />
 : <CircleDot className="w-3.5 h-3.5" />
 const categoryColor = item.category ==='bug' ?'bg-bad/10 text-bad/70 border-bad/20'
 : item.category ==='suggestion' ?'bg-brand/10 text-brand/70 border-brand/20'
 : item.category ==='expectation_mismatch' ?'bg-warn/10 text-warn/70 border-warn/20'
 :'bg-surface-alt/10 text-muted/70 border-soft/20'
 return (
 <div key={`archived-${fid}-${idx}`} className="border border-soft/30 rounded-lg bg-primary/20 p-3 opacity-75">
 <div className="flex items-center gap-2">
 <Badge className={`${categoryColor} text-xs`}>
 <span className="flex items-center gap-1">{categoryIcon} {item.category.replace('_','')}</span>
 </Badge>
 <Badge className="bg-ok/10 text-ok/70 border-ok/20 text-xs">{item.status}</Badge>
 <span className="flex-1 text-secondary/70 text-sm truncate">{item.expected}</span>
 {item.archived_at && (
 <span className="text-muted text-xs whitespace-nowrap">
 archived {new Date(item.archived_at).toLocaleDateString()}
 </span>
 )}
 </div>
 </div>
 )
 })}
 </div>
 )}
 </CardContent>
 )}
 </Card>
 </TabsContent>
 </Tabs>
 </div>
 
 {/* Floating Toast Notification */}
 {toastMessage && (
 <div 
 className={`fixed top-6 right-6 z-50 px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-2 fade-in duration-300 ${
 toastMessage.type ==='success' 
 ?'bg-ok text-on-brand' 
 :'bg-bad text-on-brand'
 }`}
 >
 {toastMessage.type ==='success' ? (
 <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
 ) : (
 <XCircle className="w-5 h-5 flex-shrink-0" />
 )}
 <span className="font-medium">{toastMessage.text}</span>
 <button 
 onClick={() => setToastMessage(null)}
 className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
 >
 <XCircle className="w-4 h-4" />
 </button>
 </div>
 )}
 </div>
 )
}

